import React from "react";
import { RefreshCw, CalendarRange, ChevronDown } from "lucide-react";
import { Menu } from "@headlessui/react";
import { CandidateDashboardFilter, DateRangeKey } from "../Types/CandidateIntelligenceTypes";

export const DATE_RANGES: { key: DateRangeKey; label: string }[] = [
  { key: "TODAY", label: "Today" },
  { key: "7", label: "Last 7 days" },
  { key: "30", label: "Last 30 days" },
  { key: "90", label: "Last 90 days" },
  { key: "YEAR", label: "Last year" },
  { key: "CUSTOM", label: "Custom…" },
  { key: "ALL", label: "All time" },
];

interface FilterBarProps {
  filter: CandidateDashboardFilter;
  onFilterChange: (next: CandidateDashboardFilter) => void;
  onRefresh: () => void;
  loading: boolean;
}

export const FilterBar: React.FC<FilterBarProps> = ({ filter, onFilterChange, onRefresh, loading }) => {
  const dateLabel = DATE_RANGES.find((r) => r.key === filter.dateRange)?.label ?? "Last 30 days";
  const activeCount =
    (filter.roles?.length || 0) +
    (filter.locations?.length || 0) +
    (filter.workModes?.length || 0) +
    (filter.jobTypes?.length || 0) +
    (filter.keyword ? 1 : 0);

  const clearAll = () =>
    onFilterChange({
      dateRange: filter.dateRange ?? "30",
      roles: [],
      locations: [],
      workModes: [],
      jobTypes: [],
      keyword: "",
    });

  const chips: { label: string; remove: () => void }[] = [
    ...(filter.roles || []).map((r) => ({ label: `Role: ${r}`, remove: () => removeFrom("roles", r) })),
    ...(filter.locations || []).map((r) => ({ label: `Location: ${r}`, remove: () => removeFrom("locations", r) })),
    ...(filter.workModes || []).map((r) => ({ label: `Mode: ${r}`, remove: () => removeFrom("workModes", r) })),
    ...(filter.jobTypes || []).map((r) => ({ label: `Type: ${r}`, remove: () => removeFrom("jobTypes", r) })),
  ];

  const removeFrom = (key: "roles" | "locations" | "workModes" | "jobTypes", value: string) => {
    onFilterChange({ ...filter, [key]: (filter[key] || []).filter((v) => v !== value) });
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Date range */}
      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-slate-200 transition hover:border-cyan-500/50">
          <CalendarRange size={16} className="text-cyan-400" />
          {dateLabel}
          <ChevronDown size={14} className="opacity-60" />
        </Menu.Button>
        <Menu.Items className="absolute left-0 z-50 mt-2 w-48 origin-top-left rounded-xl border border-slate-700 bg-slate-900 p-1.5 shadow-2xl focus:outline-none">
          {DATE_RANGES.map((r) => (
            <Menu.Item key={r.key}>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => onFilterChange({ ...filter, dateRange: r.key })}
                  className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${
                    filter.dateRange === r.key
                      ? "bg-cyan-500/20 text-cyan-200"
                      : active
                        ? "bg-slate-800 text-slate-100"
                        : "text-slate-300"
                  }`}
                >
                  {r.label}
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Menu>

      {/* Custom date range */}
      {filter.dateRange === "CUSTOM" ? (
        <>
          <input
            type="date"
            value={filter.dateFrom?.slice(0, 10) ?? ""}
            onChange={(e) =>
              onFilterChange({ ...filter, dateFrom: e.target.value ? `${e.target.value}T00:00:00` : undefined })
            }
            className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 focus:border-cyan-500"
          />
          <span className="text-xs text-slate-500">to</span>
          <input
            type="date"
            value={filter.dateTo?.slice(0, 10) ?? ""}
            onChange={(e) =>
              onFilterChange({ ...filter, dateTo: e.target.value ? `${e.target.value}T23:59:59` : undefined })
            }
            className="rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-200 focus:border-cyan-500"
          />
        </>
      ) : null}

      {/* Active chips */}
      {chips.map((chip) => (
        <button
          key={chip.label}
          type="button"
          onClick={chip.remove}
          className="flex items-center gap-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-3 py-1.5 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/20"
        >
          {chip.label} <span className="text-cyan-400">×</span>
        </button>
      ))}

      {activeCount > 0 ? (
        <button
          type="button"
          onClick={clearAll}
          className="rounded-full border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-semibold text-rose-300 hover:bg-rose-500/20"
        >
          Clear all ({activeCount})
        </button>
      ) : null}

      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="ml-auto flex items-center gap-2 rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm font-semibold text-slate-300 transition hover:border-cyan-500/50 hover:text-cyan-300 disabled:opacity-50"
      >
        <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
        Refresh
      </button>
    </div>
  );
};